import { FC } from "react";
import GallerySlider from "components/GallerySlider/GallerySlider";
import BtnLikeIcon from "components/Buttons/BtnLikeIcon";
import SaleOffBadge from "components/SaleOffBadge/SaleOffBadge";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MoodEnum } from "data/enums";
import { formatPrice } from "shared/AppFunctions/formatPrice";

export interface MultiStockCardHorizontalProps {
  id: string | number;
  className?: string;
  cover: string;
  images?: string[];
  annotation?: string;
  title: string;
  mood?: MoodEnum;
  address?: string;
  description?: string;
  price?: number;
  salePrice?: number;
  currency?: string;
  reviews_count?: number;
  rating?: number;
  reviewCount?: number;
  max_guests?: number;
  minutes_duration?: number;
  href?: string;
  isLiked?: boolean;
  onClickLike?: (e: any) => void;
  draggable?: boolean;
  onDragStart?: (e: any) => void;
  onDragEnd?: (e: any) => void;
  children?: React.ReactNode | React.ReactNode[];
  in_review?: boolean;
  stopped?: boolean;
  showStatusBadge?: boolean;
}

const MultiStockCardHorizontal: FC<MultiStockCardHorizontalProps> = ({
  id,
  className = "",
  cover,
  annotation = null,
  images = [],
  title,
  mood = null,
  address = null,
  description = null,
  price = null,
  currency = "EUR",
  salePrice = null,
  rating,
  reviewCount,
  max_guests = null,
  minutes_duration = null,
  isLiked,
  onClickLike,
  href,
  draggable = false,
  onDragStart = () => { },
  onDragEnd = () => { },
  children,
  in_review = false,
  stopped = false,
  showStatusBadge = false,
}) => {

  const { t } = useTranslation();
  const formattedPrice = price ? formatPrice(price, true, window.navigator.language, currency) : null;
  const formattedSalePrice = salePrice ? formatPrice(salePrice, true, window.navigator.language, currency) : null;

  const galleryImages = (() => {
    const imagesArray: string[] = [];

    if (cover) {
      imagesArray.push(cover);
    }

    if (images && Array.isArray(images) && images.length > 0) {
      imagesArray.push(...images);
    }

    return imagesArray.length > 0 ? imagesArray : [cover || ''];
  })();

  const renderSliderGallery = () => {
    return (
      <div className="relative w-full md:w-72 flex-shrink-0 overflow-hidden">
        <GallerySlider
          ratioClass="aspect-w-12 aspect-h-9 md:aspect-h-11"
          galleryImgs={galleryImages}
          uniqueID={`MultiStockCardHorizontal_Gallery_${id}`}
        />
        <BtnLikeIcon isLiked={isLiked} className="absolute right-3 top-3" />
        <div className="absolute left-3 top-3 flex flex-col gap-2 max-w-[70%]">
          {salePrice && <SaleOffBadge />}
          {
            in_review && (
              <div className="bg-amber-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-hourglass-half text-xs"></i>
                {t("translation:myListings.badge.inReview")}
              </div>
            )
          }
          {
            showStatusBadge && stopped && (
              <div className="bg-red-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-pause-circle text-xs"></i>
                {t("translation:myListings.badge.paused")}
              </div>
            )
          }
          {
            showStatusBadge && !stopped && !in_review && (
              <div className="bg-emerald-500 text-white text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 w-max">
                <i className="las la-check-circle text-xs"></i>
                {t("translation:myListings.badge.active")}
              </div>
            )
          }
        </div>
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div className="flex-grow p-3 sm:p-5 flex flex-col">
        {
          annotation && (
            <span className="text-sm text-neutral-500 dark:text-neutral-400 mb-1">{annotation}</span>
          )
        }
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium capitalize">
              <span className="line-clamp-1">{title}</span>
            </h2>
            {
              formattedPrice && (
                <div className="flex flex-col items-end">
                  <span className="text-base font-semibold text-secondary-700">
                    {formattedSalePrice ? formattedSalePrice : formattedPrice}
                  </span>
                  {
                    formattedSalePrice && <span className="text-sm line-through text-neutral-500">{formattedPrice}</span>
                  }
                </div>
              )
            }
          </div>
          <div className="flex items-center space-x-4 text-sm text-neutral-500 dark:text-neutral-400">
            {/*{ reviewCount && reviewCount > 0 && <StartRating reviewCount={reviewCount} point={rating} /> }
            <span>· </span>*/}
            <div className="flex items-center">
              <span className="hidden sm:inline-block  text-base">
                <i className="las la-map-marked"></i>
              </span>
              <span className="sm:ml-2"> {address}</span>
            </div>
          </div>
        </div>
        {/* <div className="w-14 border-b border-neutral-100 dark:border-neutral-800 my-4"></div> */}
        { description && (
          <div className="hidden sm:block text-sm text-neutral-500 dark:text-neutral-400 mt-4">
            <span className="line-clamp-2">
              {`${description.length < 100 ? description : description.substring(0, 100) + "..."}`}
            </span>
          </div>
        )}
        {(minutes_duration || max_guests) && (
          <div className="flex items-center space-x-8 mt-4">
            {minutes_duration && (
              <div className="flex items-center space-x-2">
                <i className="las la-clock text-lg"></i>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  {minutes_duration} minuti
                </span>
              </div>
            )}
            {max_guests && (
              <div className="flex items-center space-x-2">
                <i className="las la-user text-lg"></i>
                <span className="text-sm text-neutral-500 dark:text-neutral-400">
                  Fino a {max_guests} persone
                </span>
              </div>
            )}
          </div>
        )}
        <div className="w-14 border-b border-neutral-100 dark:border-neutral-800 my-4"></div>
        
        { children && (
          <div className="flex justify-end gap-2 items-center">
            {children}
            {/* {
              showAuthor &&
              <div className="flex items-center space-x-3 text-sm text-neutral-700  dark:text-neutral-300">
                <Avatar imgUrl={author.avatar} userName={author.displayName} />
                <span className="hidden sm:inline-block">
                  <span className="hidden sm:inline"></span>{" "}
                  {author.displayName}
                </span>
              </div>
            } */}
            {/* <ButtonSecondary
              className="text-sm  !p-3 2xl:p-10 h-[3rem]"
              // onClick={secondaryCta && (() => secondaryCta(data))}
            >
              Scopri di più
            </ButtonSecondary> */}
            {/* {
              showButtons && width > 1280 &&
              <ButtonPrimary
                data-dropdown-toggle="dropdown"
                className="text-sm  !p-3 2xl:p-10 h-[3rem]"
                onClick={() => mainCta && mainCta()}
              >
                Aggiungi al calendario
              </ButtonPrimary>
            } */}
            {/* {
              showButtons && width <= 1280 ?
                isExperienceOnCalendar ?
                  <ButtonPrimary
                    data-dropdown-toggle="dropdown"
                    className="text-sm  !p-3 2xl:p-10 h-[3rem]"
                    onClick={() => removeFromCalendar && removeFromCalendar()}
                  >
                    Rimuovi
                  </ButtonPrimary>
                  :
                  <ButtonPrimary
                    data-dropdown-toggle="dropdown"
                    className="text-sm  !p-3 2xl:p-10 h-[3rem]"
                    onClick={() => openModal && openModal()}
                  >
                    Seleziona slot
                  </ButtonPrimary>
                : null
            } */}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`nc-MustiStockCardHorizontal group relative bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 rounded-2xl overflow-hidden hover:shadow-xl transition-shadow will-change-transform ${className}`}
      data-nc-id="MustiStockCardHorizontal"
      data-uuid={id}
      data-title={title}
      draggable={draggable}
      onDragStart={(e) => {
        if (draggable && onDragStart) {
          onDragStart(e);
        }
      }}
      onDragEnd={(e) => {
        if (draggable && onDragEnd) {
          onDragEnd(e);
        }
      }}
      key={`MultiStockCardHorizontal_${id}`}
    >
      <Link to={href} className="absolute inset-0" />
      <div className="md:flex md:flex-row">
        {renderSliderGallery()}
        {renderContent()}
      </div>
    </div>
  );
};

export default MultiStockCardHorizontal;